import type { SanityImageSource } from "@sanity/image-url/lib/types/types";

import type { ResponsiveMedia } from "@/sanity/lib/utils";
import { getImageForDevice, urlForImage, urlForImageCached } from "@/sanity/lib/utils";

import type { ResponsiveImageUrls } from "./types";

/**
 * Gets responsive image URLs for mobile, tablet, and desktop devices.
 * Returns URLs with fallback logic and extracted alt text.
 * Supports fit and crop options for image transformation.
 */
export function getResponsiveImageUrls(
  media: ResponsiveMedia | null | undefined,
  options?: {
    mobileWidth?: number;
    tabletWidth?: number;
    desktopWidth?: number;
    fit?: "max" | "min" | "crop" | "clip" | "scale";
    crop?: string;
    useCache?: boolean;
  },
): ResponsiveImageUrls | null {
  if (!media) return null;

  // Check if at least one image field exists and is not null
  if (!media.image && !media.tablet && !media.mobile) {
    return null;
  }

  const { mobileWidth, tabletWidth, desktopWidth, fit, crop, useCache = true } = options || {};

  // Get image sources for each device type (with fallback logic)
  const mobileSource = getImageForDevice(media, "mobile");
  const tabletSource = getImageForDevice(media, "tablet");
  const desktopSource = getImageForDevice(media, "desktop");

  const imageBuilderFn = useCache ? urlForImageCached : urlForImage;

  // Helper to build URL with optional fit/crop/width
  const buildUrl = (
    source: SanityImageSource | null | undefined,
    width?: number,
  ): string | null => {
    if (!source) return null;
    const builder = imageBuilderFn(source);
    if (!builder) return null;
    if (fit) builder.fit(fit);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (crop) builder.crop(crop as any); // Sanity's crop accepts string but TypeScript expects CropMode
    return width ? builder.width(width).url() : builder.url();
  };

  // Generate URLs with optional sizing
  const mobileUrl = buildUrl(mobileSource, mobileWidth);
  const tabletUrl = buildUrl(tabletSource, tabletWidth);
  const desktopUrl = buildUrl(desktopSource, desktopWidth);

  // Fallback: desktop -> tablet -> mobile
  const fallbackUrl = desktopUrl || tabletUrl || mobileUrl;

  if (!fallbackUrl) return null;

  return {
    mobile: mobileUrl,
    tablet: tabletUrl,
    desktop: desktopUrl,
    fallback: fallbackUrl,
    alt: media.alt || "",
  };
}
